import { ActionAudFilterDto } from '@api/client';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { saveAuditFiltersUsed, searchAuditActions } from 'pages/private/_redux/audit/actions';
import { auditActionsSelector, auditFiltersUsedSeletor } from 'pages/private/_redux/audit/selectors';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { DatePicker, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { allUsersSelector } from 'pages/private/project-list/project-detail/_redux/selectors';
import { filterOption } from 'shared/utils/common.utils';
import LinksButton from 'shared/design-system/components/links-button';
import AppAccordion from 'shared/design-system/components/app-accordion';
import { LocationFilterEnum } from 'enums/global.enum';
import AppCard from 'shared/design-system/components/app-card';
import { format } from 'date-fns';

// Utility function per estrarre timestamp dalla request JSON
const extractTimestampFromRequest = (requestString: string): string | null => {
  if (!requestString) return null;

  try {
    const requestObj = JSON.parse(requestString);

    // Cerca campi comuni per timestamp
    const timestampFields = [
      'timestamp',
      'dataOperazione',
      'dataCreazione',
      'createdAt',
      'updatedAt',
      'date',
      'data',
    ];

    for (const field of timestampFields) {
      if (requestObj[field]) {
        return requestObj[field];
      }
    }

    // Cerca ricorsivamente in oggetti nested
    for (const key in requestObj) {
      if (typeof requestObj[key] === 'object' && requestObj[key] !== null) {
        const nestedTimestamp = extractTimestampFromRequest(JSON.stringify(requestObj[key]));
        if (nestedTimestamp) return nestedTimestamp;
      }
    }
  } catch (e) {
    // Se non è un JSON valido, cerca pattern di data nel testo
    const datePattern = /(\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2})/;
    const match = requestString.match(datePattern);
    if (match) return match[1];
  }

  return null;
};

const AuditActions: React.FC = () => {
  const dispatch = useAppDispatch();
  const auditActions = useAppSelector(auditActionsSelector);
  const allUsers = useAppSelector(allUsersSelector);
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const isSubmitting = useRef(false);
  const [chipsFormData, setChipsFormData] = useState<any[]>([]);
  const [filterParams, setFilterParams] = useState<any[]>([]);

  const { reset, handleSubmit } = useForm({
    mode: 'onChange',
    defaultValues: auditFormData,
  });

  const onSubmit = (data: any) => {
    isSubmitting.current = true;
    const formDataArray = [];

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (data[key] !== auditFormData[key]) {
          formDataArray.push([key, data[key]]);
        }
      }
    }

    setFilterParams([...formDataArray]);
    setChipsFormData([...formDataArray]);

    const auditFilter: ActionAudFilterDto = {};

    if (data.idUtente) {
      auditFilter.idUtente = data.idUtente;
    }
    if (data.dataInizio) {
      auditFilter.dataInizio = new Date(data.dataInizio);
    }
    if (data.dataFine) {
      auditFilter.dataFine = new Date(data.dataFine);
    }
    if (data.jsonFilter) {
      auditFilter.jsonFilter = data.jsonFilter;
    }
    auditFilter.page = 0;
    auditFilter.size = 9999;

    dispatch(searchAuditActions(auditFilter));

    handleSubmit(data);
  };

  const onReset = () => {
    reset(auditFormData);
    setChipsFormData([]);
    dispatch(saveAuditFiltersUsed(auditFormData));
  };

  // Funzione per ottenere il nominativo dell'utente dall'id
  const getUserName = (idUtente: number): string => {
    const user = allUsers?.find((u) => u.id === idUtente);
    return user?.nominativo || '-';
  };

  // Formatta la data dal timestamp
  const formatTimestamp = (timestamp: string | null): string => {
    if (!timestamp) return '-';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy HH:mm');
    } catch (e) {
      return '-';
    }
  };

  const AuditActionsTable = () => {
    return (
      <>
        {auditActions?.actionAudDtoList && auditActions.actionAudDtoList.length > 0 && (
          <AppCard title="Audit Actions" headerClassname="d-flex justify-content-between align-items-center">
            <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>Nominativo</th>
                    <th style={{ width: '15%' }}>Data</th>
                    <th style={{ width: '20%' }}>Azione</th>
                    <th style={{ width: '45%' }}>Dettagli Operazione</th>
                  </tr>
                </thead>
                <tbody>
                  {auditActions.actionAudDtoList.map((action: any) => {
                    return (
                      <tr key={action.id}>
                        <td>{getUserName(action.idUtente!)}</td>
                        <td>{formatTimestamp(action.date)}</td>
                        <td>{action.action || '-'}</td>
                        <td>
                          <div
                            style={{
                              maxHeight: '100px',
                              overflowY: 'auto',
                              fontSize: '0.875rem',
                              fontFamily: 'monospace',
                            }}
                          >
                            {action.request || '-'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </AppCard>
        )}
        {auditActions?.actionAudDtoList && auditActions.actionAudDtoList.length === 0 && (
          <AppCard title="Audit Actions">
            <div className="text-center py-5">
              <p className="text-muted">Nessuna azione trovata con i filtri selezionati</p>
            </div>
          </AppCard>
        )}
      </>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between mt-3">
        <h4 className="text-gray-800 my-0">Audit actions</h4>
      </div>

      <AppAccordion
        formData={auditFormData}
        location={LocationFilterEnum.AUDIT_ACTIONS}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        chipsFormData={chipsFormData}
        setChipsFormData={setChipsFormData}
        reset={reset}
      >
        <FiltersAudit onSubmit={onSubmit} handleReset={onReset} />
      </AppAccordion>
      <AuditActionsTable />
    </div>
  );
};

interface AuditFiltersProps {
  onSubmit: (formData: any) => void;
  handleReset: () => void;
}

const auditFormData: any = {
  idUtente: undefined,
  dataInizio: undefined,
  dataFine: undefined,
  jsonFilter: undefined,
};

const FiltersAudit = ({ onSubmit, handleReset }: AuditFiltersProps) => {
  const filterUsed = useAppSelector(auditFiltersUsedSeletor);
  const allUsers = useAppSelector(allUsersSelector);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState(auditFormData);

  useEffect(() => {
    setFormValues({
      idUtente: filterUsed?.idUtente || '',
      dataInizio: filterUsed?.dataInizio || undefined,
      dataFine: filterUsed?.dataFine || undefined,
    });
  }, [filterUsed]);

  const { handleSubmit, control, register, setValue, reset } = useForm({
    mode: 'onChange',
    defaultValues: auditFormData,
    values: formValues,
  });

  const Reset = () => {
    reset(auditFormData);
    handleReset();
  };

  const handleFormSubmit = async (formData: any) => {
    dispatch(saveAuditFiltersUsed(formData));
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <Form className="grid" onSubmit={handleSubmit(handleFormSubmit)}>
      <Row>
        <Col sm="3">
          {' '}
          <Form.Label>{t('User')}</Form.Label>
          <Controller
            name="idUtente"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                size="large"
                className="w-100"
                allowClear
                value={field.value}
                optionFilterProp="children"
                filterOption={filterOption}
                onChange={(value) => field.onChange(value)}
                onBlur={field.onBlur}
                placeholder={t('Select User')}
              >
                {allUsers?.map((el, index) => (
                  <Select.Option key={index} value={el.id}>
                    {el.nominativo}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </Col>
        <Col sm="3">
          <Form.Label>{t('Start date')}</Form.Label>
          <Controller
            name="dataInizio"
            control={control}
            defaultValue={undefined}
            render={({ field }) => (
              <DatePicker
                allowClear
                placeholder={`${t('project-list.start-date-placeholder')}`}
                className="w-100"
                {...field}
                ref={field.ref}
                name={field.name}
                onBlur={field.onBlur}
                format="DD/MM/YYYY"
                value={field.value ? dayjs(field.value) : undefined}
                size="large"
                onChange={(date: any) => {
                  field.onChange(date ? date.format('YYYY-MM-DD') : null);
                }}
              />
            )}
          />
        </Col>
        <Col sm="3">
          <Form.Label>{t('End date')}</Form.Label>
          <Controller
            name="dataFine"
            control={control}
            defaultValue={undefined}
            render={({ field }) => (
              <DatePicker
                allowClear
                placeholder={`${t('project-list.end-date-placeholder')}`}
                className="w-100"
                {...field}
                ref={field.ref}
                name={field.name}
                onBlur={field.onBlur}
                format="DD/MM/YYYY"
                value={field.value ? dayjs(field.value) : undefined}
                size="large"
                onChange={(date: any) => {
                  field.onChange(date ? date.format('YYYY-MM-DD') : undefined);
                }}
              />
            )}
          />
        </Col>
        <Col sm="3">
          <Form.Label>{t('JSON Filter')}</Form.Label>
          <Controller
            name="jsonFilter"
            control={control}
            render={({ field }) => (
              <Input
                type="text"
                placeholder={t('Search JSON')}
                size="large"
                allowClear
                className="w-100"
                value={field.value}
                onChange={(value) => field.onChange(value)}
              />
            )}
          />
        </Col>
      </Row>

      <div className="d-flex mt-3">
        <div className="d-flex ms-auto">
          <Button className="btn text-danger trash-btn" type="button" variant="icon" onClick={Reset}>
            <i className="fa-regular fa-trash-can me-2" />
            {t('project-list.delete-filters')}
          </Button>
          <LinksButton type="submit" className="btn-sm btn-primary btn-links px-4">
            <i className="fa-solid fa-magnifying-glass me-2" />
            {t('project-list.search')}
          </LinksButton>
        </div>
      </div>
    </Form>
  );
};

export default AuditActions;
